import { initializeBackendABTesting } from '@core/abTesting/backend/backendABTesting'
import { applyFocusVisible, disableFastClick } from '@core/accessibility'
import { logInfo } from '@core/logging'
import type { IAllWidgetParams } from '@core/types'
import { DETACHED_PLAYER_ID } from '@embed/constants'

import { BaseElement } from './BaseElement'
import { MutationMixin } from './MutationMixin'
import { ParamsMixin } from './ParamsMixin'
import { VisibilityMixin } from './VisibilityMixin'

/**
 * Base class for all FWN web components. Applies observer to rerender
 * when widget params change. Includes opt-in visibility-based lazy
 * rendering via the `lazy="true"` HTML attribute.
 */
export class FWNElement extends VisibilityMixin(
  MutationMixin(ParamsMixin(BaseElement)),
) {
  /**
   * Starts an experiment fetch once business_id is known. Channel_id is
   * included when already on the element; otherwise React initializes after
   * the channel API resolves both ids. Subclasses that render outside
   * `performRender` (e.g. visibility-gated embed feed) should call this when
   * they actually paint.
   */
  protected initializeExperiments(): void {
    const { business_id: businessId, channel_id: channelId } = this.params
    if (!businessId) {
      return
    }
    void initializeBackendABTesting({
      business_id: businessId,
      ...(channelId ? { channel_id: channelId } : {}),
    })
  }

  /**
   * Performs the render of the component. This wrapper allows to override the render method by parent classes.
   */
  protected performRender(): void {
    this.initializeExperiments()
    this.render()
  }

  protected override onAttributeChange(): void {
    this.performRender()
  }

  /**
   * Whether the widget renders as a pinned floating player (`mode="pinned"`),
   * which floats above the embedding page instead of sitting in its flow.
   */
  protected get isPinnedMode(): boolean {
    return (this.params as IAllWidgetParams).mode === 'pinned'
  }

  /**
   * Sets an inline style on the host which reserves layout space for the
   * widget. A pinned widget has no box in the page flow, so the property is
   * removed instead of set to never shift the embedding page's layout.
   */
  protected setSpaceReservingStyle(
    property: 'height' | 'aspect-ratio' | 'margin-bottom',
    value: string,
  ): void {
    if (this.isPinnedMode) {
      this.style.removeProperty(property)
      return
    }
    this.style.setProperty(property, value)
  }

  /**
   * Invoked when the component is added to the document's DOM.
   */
  override connectedCallback(): void {
    super.connectedCallback()
    this.performRender()
    if (this.shadowRoot) {
      applyFocusVisible(this.shadowRoot)
      disableFastClick(this)
    }
  }

  /**
   * Invoked when the component is removed from the document's DOM.
   */
  override disconnectedCallback(): void {
    super.disconnectedCallback()

    // If there is a fw-player element on the page, we do not unmount the
    // component immediately. For SPA, the fw-player needs to be kept to
    // continue playback in the floating player.
    import('@assets/webcomponents/bases/fwnRootCache').then(
      ({ cacheRoot, unmountComponent, unmountComponentsInCache }) => {
        if (this.shadowRoot) {
          if (document.getElementById(DETACHED_PLAYER_ID)) {
            logInfo(`fw-player element found, cache root ${this.widgetId}`)
            cacheRoot(this.shadowRoot, this.widgetId)
          } else {
            logInfo(`Unmounting current component ${this.widgetId}`)
            unmountComponent(this.shadowRoot, this.widgetId)
            // After unmounting the current component, we need to clean up all the
            // cached roots to prevent memory leak.
            unmountComponentsInCache()
          }
        }
      },
    )
  }
}
